import type { Remedy, StaleReason } from "../../../lib/queries/startlists";

/**
 * Czech copy for the staleness keys the server reports.
 *
 * Same split as the dashboard alerts: the server ships machine keys and
 * the page owns the wording. And the same hazard — adding a reason in Go
 * without adding it here renders nothing at all, because the lookup has
 * to tolerate a key it does not know. Treat the two edits as one change.
 */
export interface StaleCopy {
  /** What the list no longer matches — completes „Startovní listina
   * neodpovídá …". Two reasons with different subjects fall back to the
   * broader one. */
  subject: "přihláškám" | "soutěži";
  /** What drifted, as a COMPLETE sentence.
   *
   * Complete on purpose: these used to end mid-sentence on „…Doplňte je
   * tlačítkem", which read fine alone and fell apart the moment a second
   * reason followed — two half-sentences ran together with no full stop,
   * and if the other reason was a manual one there was no button at the
   * end of the dangling instruction either. */
  text: string;
  /** The instruction that introduces the button, used when this reason is
   * the only thing outstanding. Absent for reasons a button cannot fix. */
  action?: string;
}

const COPY: Record<StaleReason, StaleCopy> = {
  days_missing: {
    subject: "soutěži",
    text: "Soutěž se koná i ve dnech, které listina nemá.",
    action: "Doplňte je tlačítkem",
  },
  days_orphaned: {
    subject: "soutěži",
    text:
      "Listina má den, kdy se soutěž už nekoná, a je v něm rozvržený program. " +
      "Den je označený „Mimo termín soutěže“. Přesuňte z něj kategorie na den, " +
      "kdy se soutěž koná, a prázdný den pak zmizí sám.",
  },
  days_extra: {
    subject: "soutěži",
    text:
      "Listina má den, kdy se soutěž nekoná, a nic v něm není. " +
      "Je označený „Mimo termín soutěže“.",
    action: "Odeberete ho tlačítkem",
  },
  category_split: {
    subject: "přihláškám",
    text: "Některá soutěžní kategorie je rozdělená do dvou bloků.",
    action: "Srovnáte to tlačítkem",
  },
  category_misplaced: {
    subject: "přihláškám",
    // Both directions land here: merging moves entries into the
    // survivor's category, splitting moves them out into a new one.
    // Naming only the split described half the cases as the whole.
    text: "Některé přihlášky jsou v bloku kategorie, do které už nepatří.",
    action: "Přeskupíte je tlačítkem",
  },
  withdrawn: {
    subject: "přihláškám",
    text: "Někdo se odhlásil a stále je v listině.",
    action: "Odeberete ho tlačítkem",
  },
  empty_block: {
    subject: "přihláškám",
    text: "V listině je kategorie, ve které nikdo nestartuje.",
    action: "Odeberete ji tlačítkem",
  },
  unplaced: {
    subject: "přihláškám",
    text: "Některé přihlášky ještě nejsou v listině.",
    action: "Doplňte je tlačítkem",
  },
};

export function staleCopy(reason: StaleReason): StaleCopy | undefined {
  return COPY[reason];
}

/** „Startovní listina neodpovídá …" — přihláškám, or the broader soutěži. */
export function staleTitle(reasons: StaleReason[]): string {
  const subjects = new Set(reasons.map((r) => staleCopy(r)?.subject).filter(Boolean));
  const subject = subjects.size === 1 ? [...subjects][0] : "soutěži";
  return `Startovní listina neodpovídá ${subject}.`;
}

/**
 * The one sentence that introduces the button, or null when nothing the
 * button can fix is outstanding.
 *
 * With a single reason it is that reason's own wording — „Doplňte je
 * tlačítkem". With several it has to speak for all of them at once, and
 * naming one of the problems would be worse than naming none.
 *
 * Which reasons a button can fix comes from the SERVER
 * (`stale_remedies`), not from a table here. This file used to carry its
 * own `fix` per reason, and a page that keeps its own opinion about what
 * the server will do is a page that will eventually be wrong about it —
 * it already was, disabling the button for a build the server was
 * willing to perform.
 *
 * A list can have both a manual problem and a reconcilable one, and the
 * button still belongs on screen for the half it does fix. Deciding the
 * two together is what once left „Doplňte je tlačítkem" with no button
 * after it.
 */
export function staleAction(
  reasons: StaleReason[],
  remedies: Partial<Record<StaleReason, Remedy>> | undefined,
): string | null {
  const build = reasons.filter((r) => remedies?.[r] === "build");
  if (build.length === 0) return null;
  if (build.length === 1) return staleCopy(build[0])?.action ?? "Srovnáte to tlačítkem";
  return "Srovnáte to tlačítkem";
}

/**
 * What the reconcile is called, everywhere.
 *
 * One name, because it is one action: it adds late přihlášky, takes off
 * odhlášky, adds a day the competition gained and regroups after a merge
 * or a split. It was „Doplnit přihlášky", which named a quarter of that
 * and made the button look like it was ignoring the problem it had just
 * been offered for — „Doplnit přihlášky" is a strange answer to „the
 * competition runs on a day the list has not got".
 */
export const ACTION_LABEL = "Aktualizovat";

/**
 * The same button on a list that has nothing in it yet.
 *
 * Every competition owns a start list from the moment it exists, so the
 * first press is not an update of anything — it is the build. Calling it
 * „Aktualizovat" over an empty page would ask the organizer to update a
 * document they can see is blank.
 */
export const FIRST_BUILD_LABEL = "Sestavit listinu";

/**
 * Taking a published list back off the wire.
 *
 * It was „Stáhnout", which in a web app reads as *download* first and
 * only then as *withdraw* — on a page that sits next to „Náhled" and
 * will grow a PDF export, that is a destructive action wearing the
 * costume of a harmless one. „Zrušit zveřejnění" can only mean the one
 * thing, and it names the exact state it returns to, since the button
 * beside it is „Zveřejnit".
 *
 * Deliberately not „Odhlásit" or anything built on *stáhnout*: those
 * belong to a přihláška (see the glossary — `withdrawn` is
 * **Odhlášeno**), and reusing them here would collide with an operation
 * on the very rows this list is made of.
 */
export const UNPUBLISH_LABEL = "Zrušit zveřejnění";
